import { queryOptions } from '@tanstack/vue-query'
import { fetch } from './api/fetchRegions'
import { fetchProviders } from './api/fetchUtilityProvider'
import { fetchTariffNames } from './api/fetchtariffNames'
import { fetchTariffs } from './api/fetchTariffs'
import { fetchTariffYears } from './api/fetchTariffYears'
import { type MaybeRefOrGetter, type Ref, toValue, type WritableComputedRef } from 'vue'
import { fetchTOUProfileConfig } from './api/fetchTOUProfiles'
import { fetchAssetTariffDashboard } from './api/fetchAssetTariffsDashboard'
import type { PaginationParameters } from '@/composables/query-parameters'
import { tariffQueryKeys } from './query-keys'
import type { SharedPaginationOptions } from '@/shared/types'

type TariffPaginationOptions = Parameters<typeof tariffQueryKeys.tariffs.paginated>[0]

export const filterQueries = {
  fetchRegion: () => ({
    queryKey: tariffQueryKeys.rigions.all(),
    queryFn: () => fetch(),
    meta: {
      logger: 'features/tariffs/fetchRegion'
    },
  }),

  fetchProviders: (regions?: MaybeRefOrGetter<number[]>) => queryOptions({
    queryKey: tariffQueryKeys.tariffs.providers(regions),
    queryFn: () => fetchProviders(toValue(regions)),
    enabled: () =>{
      const val =  toValue(regions)
      return Array.isArray(val) && val.length > 0
    },
    meta: {
      logger: 'features/tariffs/fetchProviders'
    }
  }),

  fetchTariffNames: (providers: MaybeRefOrGetter<number[]>) => queryOptions({
    queryKey: tariffQueryKeys.tariffs.tariffNames(providers),
    queryFn: () => fetchTariffNames(toValue(providers)),
    enabled: () => {
      const val = toValue(providers)
      return Array.isArray(val) && val.length > 0
    },
    meta: {
      logger: 'features/tariffs/fetchTariffNames'
    }
  }),

  fetchTariffYears: (
    ids: MaybeRefOrGetter<number[]>,
    regions: MaybeRefOrGetter<number[]>,
    providers: MaybeRefOrGetter<number[]>,
  ) => queryOptions({
    queryKey: tariffQueryKeys.tariffs.tariffYears(ids, regions, providers),
    queryFn: () => fetchTariffYears({
      tariffIds:   toValue(ids),
      provinceIds: toValue(regions),
      providerIds: toValue(providers),
    }),
    enabled: () => toValue(regions).length > 0,
    meta: {
      logger: 'features/tariffs/fetchTariffYears'
    }
  })
} as const

export const mainQueries = {
  fetchTariffs: (options: TariffPaginationOptions) => queryOptions({
    queryKey: tariffQueryKeys.tariffs.paginated(options),
    queryFn: () => fetchTariffs(
      toValue(options.ids),
      toValue(options.yearOfTariff),
      toValue(options.page),
      toValue(options.pageSize),
      toValue(options.provinceIds),
      toValue(options.providerIds),
    ),
    enabled: () => {
      const year = toValue(options.yearOfTariff)
      const regions = toValue(options.provinceIds)
      return year.startsWith('Compare') || (Array.isArray(regions) && regions.length > 0)
    },
    meta: {
      logger: 'features/tariffs/fetchTariffs'
    }
  }),

  fetchTOUProfileConfig: (profileId: MaybeRefOrGetter<number>) => queryOptions({
    queryKey: tariffQueryKeys.timeOfUseConfigs.all(profileId),
    queryFn: () => fetchTOUProfileConfig(toValue(profileId)),
    enabled: () => {
      const val = toValue(profileId)
      return val > 0
    },
    meta: {
      logger: 'features/tariffs/TOUProfile'
    }
  }),

  fetchAssetTariffDashboard: (params: SharedPaginationOptions) => queryOptions({
    queryKey: tariffQueryKeys.assetTariffDashboard.paginated(params),
    queryFn: () => fetchAssetTariffDashboard({
      page: toValue(params.page),
      pageSize: toValue(params.pageSize),
    }),
    meta: {
      logger: 'features/tariffs/AssetTariffDashboard'
    }
  })
}
